<template>
  <div class="status">
    <div v-if="isDataUpdating"
         class="page-cover-loader">
      <base-loader />
    </div>
    <div class="container" v-if="snapshotsMissing">
      <div class="rank-zone__no-data">Looks like you don’t have any ranking data to report.</div>
    </div>
    <div class="container" v-else-if="!isDataFetched">
      <div class="rank-zone__loader">
        <base-loader />
      </div>
    </div>
    <div class="container" v-else>
      <div class="status__row">
        <base-content-block class="status__row-item"
                            title="Status">
          <div v-if="!isMonthlySearchVolume"
               class="no-data-block">
            Not enough data for drawing a chart
          </div>
          <div v-else
               class="status__chart-wrapper">
            <PieChart :data="monthlySearchVolume.items"
                      :footerItem="monthlySearchVolume.footer" class="charts__pie"/>
          </div>
        </base-content-block>
        <base-content-block class="status__row-item"
                            title="Share of voice">

          <div v-if="!shareOfVoice.length"
               class="no-data-block">
            Not enough data for drawing a chart
          </div>
          <div v-else
               class="status__chart-wrapper">
            <BarChart :list1="shareOfVoice" :list2="[]" :date1="snapshot_date" :date2="''" class="charts__pie"/>
          </div>
        </base-content-block>
      </div>

      <div class="table-wrapper">
        <div class="table-wrapper__header status__table-controls">
          <base-select class="status__select"
                       v-model="snapshot_date"
                       :showTopBorder="true"
                       :options="formattedSnapshots"
                       @change="onSnapshotChanged()"/>
        </div>
        <base-table-collapsible class="status__table"
                                :config="config"
                                :data="tableData"
                                :count="tableData.length"
                                ref="table"/>
      </div>
    </div>
    <modal name="url"
           v-if="selectedClient"
           v-bind="urlModalProps">
      <UrlPopup :title="selectedUrl.label"
                :client="selectedClient._id"
                :url="selectedUrl._id"
                :date="snapshot_date"
                :selectedAdvancedOptions="selectedAdvancedOptions"
                @close="hidePopup" />
    </modal>
  </div>
</template>

<script>
import _ from 'lodash'
import filters from '@/common/Filters'
import baseModalProps from '@/common/BaseModalProps'
import PieChart from '@/features/reports/common/PieChart'
import BarChart from '@/features/reports/common/BarChart'
import UrlPopup from '@/features/reports/common/UrlPopup'
import { mapActions, mapState } from 'vuex'
import { convertAdvancedOptionsToFilters } from '@/features/reports/common/advanced-options/config'
import {
  baseComponent,
  topLevelTableConfig,
  secondLevelTableConfig,
  thirdLevelTableConfig,
  fourthLevelTableConfig
} from '@/features/reports/common/Mixins'
import { moveOtherToEnd } from '@/features/reports/common/Helper'

/**
 * Status page.
 * Used in RankZone
 */
export default {
  name: 'Status',
  mixins: [baseComponent, topLevelTableConfig, secondLevelTableConfig, thirdLevelTableConfig, fourthLevelTableConfig],
  props: {
    /**
     * Advanced options from parent element
     */
    selectedAdvancedOptions: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      // snapshot date selected from table header select
      snapshot_date: '',
      // url that should be shown in UrlPopup
      selectedUrl: {},
      // snapshot list for the snapshot dropdown
      formattedSnapshots: []
    }
  },
  computed: {
    ...mapState('rankZoneCommon', {
      /**
       * @alias expanedUrl
       * */
      expandedItem: 'expandedItem',
      /**
       * path to constant object holds paths to corresponding page specific store actions
       * to set received form BE keyword from common store into corresponding store
       * */
      KEYWORDS_PATH: 'KEYWORDS_PATH'
    }),
    ...mapState('rankZoneStatus', {
      /**
       * prepare data for {@link PieChart}
       * */
      monthlySearchVolume (state) {
        let total = 0
        const statusData = Object.keys(state.monthly_search_volume).map(name => {
          const msv = state.monthly_search_volume[name]
          total += msv
          return {
            name,
            value: msv
          }
        })
        return {
          footer: {
            name: 'Total MSV:',
            value: total
          },
          items: statusData
        }
      },
      isMonthlySearchVolume () {
        return this.monthlySearchVolume.items && this.monthlySearchVolume.items.length
      },
      /**
       * prepare share_of_voice data for {@link BarChart}
       * @param state
       */
      shareOfVoice (state) {
        return moveOtherToEnd(state.share_of_voice)
      },
      tableData (state) {
        return state.statuses
      },
      /**
       * return 'unfiltered_totals' store array as master sum for table header
       */
      masterSum: 'unfiltered_totals'
    }),
    /**
     * modal props for {@link UrlPopup}
     * */
     urlModalProps: function () {
        const urlModalProps = {
          width: 980
        }
        return { ...baseModalProps, ...urlModalProps }
      },
    /**
     * Table config
     */
      config () {
        return [
          this.topLevelConfig,
          this.secondLevelConfig,
          this.thirdLevelConfig,
          this.forthLevelConfig
        ]
      }
  },
  watch: {
    selectedClient () {
      this.isDataFetched = false
    },
    selectedAdvancedOptions: {
      async handler () {
        this.isDataFetched && await this.applyAdvancedOptions()
      },
      deep: true
    },
    /**
     * trigger for loading data
     */
    snapshots: {
      handler (snapshots) {
         if (!this.selectedClient || !this.snapshotsPresent) {
          return
        }
        this.mapSnapshots()
        this.$nextTick(this.loadPageData)
      },
      immediate: true
    },
    /**
     * reset advanced options from copied if necessary and starts {@link #updateDataCall}
     * */
    advancedOptions () {
      if (this.advancedFilters) {
        this.restoreAdvancedFilters()
      }
    },
    /**
     * watcher for {@link #expanedUrl} from store
     * starts loading keyword from backend {@link #getKeywordsCall}
     * @param item
     */
    expandedItem (item) {
      if (item) {
        this.getKeywords(item._id)
      }
    }
  },
  methods: {
    ...mapActions('rankZoneStatus', ['getStatusData']),
    ...mapActions('rankZoneCommon', ['getKeywordsByUrlIdData']),
    async loadPageData () {
      const advancedFilters = convertAdvancedOptionsToFilters(this.selectedAdvancedOptions)
      const data = await this.getStatusData({ clientId: this.clientId, filters: { advancedFilters } })
      data && (this.isDataFetched = true)
    },
    /**
     * snapshot dropdown callback
     */
    onSnapshotChanged: async function () {
      this.$refs['table'].collapseTable()
      await this.updateData()
    },
    /**
     * prepare snapshots for dropdown
     */
    mapSnapshots () {
      this.snapshot_date = ''
      this.snapshot_date = this.snapshots[0]
      this.formattedSnapshots = this.snapshots.map(snapshot => {
        return {
          label: this.$options.filters.formattedDate(snapshot),
          value: snapshot
        }
      })
    },
    /**
     * open {@link UrlPopup}
     */
    openPopup (data) {
      this.selectedUrl = {
        _id: data._id,
        label: data.label
      }
      this.$modal.show('url')
    },
     /**
     * hide {@link UrlPopup}
     */
    hidePopup () {
      this.$modal.hide('url')
      this.selectedUrl = {}
    },
    /**
     * invoked whether from {@link AdvancedOptions} component or from {@link advancedFiltersMixins}
     * @param options
     * @returns {Promise<void>}
     */
    applyAdvancedOptions: async function () {
      this.$refs['table'] && this.$refs['table'].collapseTable()
      await this.updateData()
    },
    /**
     * @alias updateDataCall
     * update data after applying advanced filters or changing snapshot
     */
    async updateData () {
      const advancedFilters = convertAdvancedOptionsToFilters(this.selectedAdvancedOptions)
      const snapshot_date = this.snapshot_date ? this.snapshot_date : null
      const filters = Object.assign({}, { snapshot_date, advancedFilters })
      if (this.isDataFetched) {
        this.isDataUpdating = true
      }
      await this.getStatusData({ clientId: this.clientId, filters })
      this.isDataFetched = true
      this.isDataUpdating = false
    },
    /**
     * @alias getKeywordsCall
     * @param urlId
     */
    getKeywords (urlId) {
      const advancedFilters = convertAdvancedOptionsToFilters(this.selectedAdvancedOptions)
      const snapshot_date = this.snapshot_date ? this.snapshot_date : null
      const filters = Object.assign({}, { snapshot_date, advancedFilters, url_id: urlId })
      this.getKeywordsByUrlIdData({ clientId: this.clientId, filters, screen: this.KEYWORDS_PATH.STATUS })
    }
  },
  components: {
    PieChart,
    BarChart,
    UrlPopup
  },
  filters: {
    ...filters
  }
}
</script>

<style scoped lang="scss">

  .status {
    &__row {
      display: flex;
      align-items: stretch;
      justify-content: space-between;
      margin-bottom: 14px;

      &-item {
        flex: 1 1 100%;
        overflow: hidden;

        &:first-child {
          margin-right: 14px;
        }
      }
    }

    &__chart-wrapper {
      width: 100%;
      padding: 15px 0 30px 40px;
      box-sizing: border-box;
    }

    &__table {
      flex: 1 1;
    }

    &__table-controls {
      display: flex;
      align-items: center;
      justify-content: flex-end
    }

    &__select {
      flex: 0 0 160px;
    }

    &__advanced {
      margin: 14px 0;
    }
  }
</style>
